import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { loadSearchResultFromStorage } from '../../../store/insuranceSearchStore';
import { parsePathString } from '../../../utils/insuranceSearchUtil';

/**
 * セッションストレージのキー
 * ブラウザリロード時に検索結果を復元するために使用
 */
const SESSION_STORAGE_KEY = 'insuranceHikiukeDetailSelectedResult';

/**
 * タブのタイトル（固定）
 */
const APP_TITLE = 'ASFiNDER';

/**
 * セッションストレージから検索結果を読み込む
 * 
 * ブラウザリロード時に以前選択した検索結果を復元するために使用。
 * パースエラー時はストレージをクリアして安全に失敗する。
 * 
 * @returns {Object|null} 保存された結果データ、存在しない場合はnull
 */
const loadResultFromSessionStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    const storedValue = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    return storedValue ? JSON.parse(storedValue) : null;
  } catch (error) {
    console.error('sessionStorageからの読み込みに失敗しました:', error);
    // 破損したデータをクリア
    try {
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (removeError) {
      console.error('sessionStorageのクリアに失敗しました:', removeError);
    }
    return null;
  }
};

/**
 * セッションストレージに検索結果を保存する
 * 
 * ブラウザリロード後も選択した検索結果を維持するために使用。
 * 
 * @param {Object} resultData - 保存する検索結果データ
 */
const saveResultToSessionStorage = (resultData) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(resultData));
  } catch (error) {
    console.error('sessionStorageへの保存に失敗しました:', error);
  }
};

/**
 * localStorage/sessionStorageから読み込んだ生データを検索結果配列に正規化する。
 *
 * 保存形式の揺れに対応するため、以下のフォーマットをすべて受け付ける:
 * - 検索結果配列そのもの
 * - `{ results: [...] }`
 * - `{ "0": {...}, "1": {...} }` のような数値キーオブジェクト
 * - 単一の検索結果オブジェクト
 *
 * @param {any} rawPayload - ストレージから読み込んだ生データ
 * @returns {Array<Object>} 検索結果配列
 */
const normalizeResultsPayload = (rawPayload) => {
  if (!rawPayload) return [];

  if (Array.isArray(rawPayload)) {
    return rawPayload.filter(Boolean);
  }

  if (Array.isArray(rawPayload.results)) {
    return rawPayload.results.filter(Boolean);
  }

  const numericKeys = Object.keys(rawPayload)
    .filter((key) => /^\d+$/.test(key))
    .sort((a, b) => Number(a) - Number(b));

  if (numericKeys.length > 0) {
    return numericKeys.map((key) => rawPayload[key]).filter(Boolean);
  }

  if (rawPayload.pdfUrl) {
    return [rawPayload];
  }

  return [];
};

/**
 * 引受目安検索結果から保険会社名を取得するヘルパー関数。
 *
 * @param {Object} result - 引受目安検索結果
 * @returns {string} 保険会社名。取得できない場合は「保険会社名不明」
 */
const getCompanyName = (result) => {
  if (result.companyName) return result.companyName;
  if (result.company) return result.company;
  if (result.pdfUrl) return parsePathString(result.pdfUrl).company || '保険会社名不明';
  return '保険会社名不明';
};

/**
 * 引受目安詳細画面のセッション・状態管理カスタムフック
 * 
 * 複数の引受目安PDFを横断的に閲覧するための検索結果読み込み・管理を担当する。
 * 検索結果データの読み込み・保存・グループ化や、アクティブな会社/傷病の切り替え、
 * ドキュメント情報アコーディオンの管理を行う。
 * 
 * データソースの優先順位:
 * 1. sessionStorage（ブラウザリロード時の復元用）
 * 2. insuranceSearchStore（検索画面からの遷移時）
 * 
 * @param {Object} options - オプション
 * @param {Function} options.onError - エラー発生時のコールバック関数
 * @param {Function} options.setCurrentPage - PDFの現在ページを設定する関数
 * @param {Function} options.initializeHighlight - ハイライト情報を初期化する関数
 * @returns {Object} セッション関連の状態とハンドラ
 * @returns {Object|null} returns.detailResult - 検索結果の詳細データ
 * @returns {string} returns.blobName - PDF BlobのURL/パス
 * @returns {string|null} returns.currentQueryId - 現在のクエリID（フィードバック用）
 * @returns {Object} returns.documentInfo - ドキュメント情報（保険会社名など）
 * @returns {boolean} returns.isMobile - モバイル表示かどうか（画面幅1024px以下）
 * @returns {boolean} returns.isDocInfoAccordionOpen - ドキュメント情報アコーディオンの開閉状態
 * @returns {Function} returns.toggleDocInfoAccordion - アコーディオン開閉トグル関数
 * @returns {Array<Object>} returns.groupedResults - 保険会社ごとにグループ化した検索結果一覧
 * @returns {string} returns.activeCompany - 現在選択中の保険会社名
 * @returns {Array<Object>} returns.activeCompanyResults - 現在選択中の保険会社に紐づく検索結果一覧
 * @returns {Object} returns.selectedDiseaseByCompany - 保険会社ごとの選択中傷病キーマップ
 * @returns {Function} returns.activateCompany - 保険会社タブを選択する関数
 * @returns {Function} returns.activateDisease - 傷病タブを選択する関数
 * @returns {Function} returns.setDetailResult - 詳細結果を設定する関数
 * @returns {Function} returns.setBlobName - BlobNameを設定する関数
 */
export const useInsuranceHikiukeSession = ({
  onError,
  setCurrentPage,
  initializeHighlight,
}) => {
  /**
   * 検索結果の詳細データ
   * APIから取得した検索結果の全情報を保持
   */
  const [detailResult, setDetailResult] = useState(null);

  /**
   * PDF BlobのURL/パス
   * PDFViewerコンポーネントでPDFを読み込むために使用
   */
  const [blobName, setBlobName] = useState('');

  /**
   * 現在のクエリID
   * フィードバック送信時にどの検索に対するフィードバックかを識別するために使用
   */
  const [currentQueryId, setCurrentQueryId] = useState(null);

  /**
   * ドキュメント情報
   * ツールバーに表示する保険会社名、傷病名などの情報
   */
  const [documentInfo, setDocumentInfo] = useState({
    insuranceCompany: '',
    diseaseName: '',
  });

  /**
   * モバイル表示フラグ
   * 画面幅1024px以下の場合にtrueになり、UIレイアウトを切り替える
   */
  const [isMobile, setIsMobile] = useState(false);

  /**
   * ドキュメント情報アコーディオンの開閉状態
   * モバイル表示時にドキュメント情報を折りたたみ表示するために使用
   */
  const [isDocInfoAccordionOpen, setIsDocInfoAccordionOpen] = useState(false);

  /**
   * 全検索結果一覧。会社・傷病ごとのタブ表示や選択状態管理に使用
   */
  const [allResults, setAllResults] = useState([]);

  /**
   * 現在選択中の会社タブの保険会社名
   */
  const [activeCompany, setActiveCompany] = useState('');

  /**
   * 会社ごとの選択中傷病キーを保持するマップ
   * key: 保険会社名、value: 選択中の傷病キー
   */
  const [selectedDiseaseByCompany, setSelectedDiseaseByCompany] = useState({});

  /**
   * デフォルトのドキュメントタイトル
   * アンマウント時にブラウザタブのタイトルを元に戻すために使用
   */
  const defaultDocumentTitleRef = useRef(
    typeof document !== 'undefined' ? document.title : APP_TITLE
  );

  /**
   * ブラウザタブのタイトルを更新するEffect
   * 保険会社名が変更されたらタイトルを「{保険会社名} | ASFiNDER」形式に更新
   * アンマウント時は元のタイトルに復元
   */

  /**
   * 会社名変更時に検索結果をグループ化して表示順序を管理するメモ化処理
   */
  const groupedResults = useMemo(() => {
    const groups = new Map();
    allResults.forEach((result, idx) => {
      const companyName = getCompanyName(result);
      const diseaseName = result.diseaseName || '傷病名不明';
      const normalized = {
        ...result,
        _key: `${result.id ?? idx}-${companyName}-${diseaseName}-${result.pdfUrl ?? ''}`,
        companyName,
        diseaseName,
      };

      if (!groups.has(companyName)) {
        groups.set(companyName, []);
      }
      groups.get(companyName).push(normalized);
    });

    return Array.from(groups.entries()).map(([companyName, results]) => ({
      companyName,
      results,
    }));
  }, [allResults]);

  /**
   * 現在選択中の会社タブの検索結果一覧
   */
  const activeCompanyResults = useMemo(() => {
    const matchedGroup = groupedResults.find((group) => group.companyName === activeCompany);
    return matchedGroup?.results ?? [];
  }, [groupedResults, activeCompany]);

  /**
   * 現在選択中の検索結果をアクティブな表示状態に適用する。
   *
   * PDF読み込み用のblobName設定・ページ番号復元・ハイライト初期化を行う。
   *
   * @param {Object} resultData - 表示対象の検索結果
   */
  const applyActiveResult = useCallback((resultData) => {
    if (!resultData) return;

    setDetailResult(resultData);
    setCurrentQueryId(resultData.queryId || null);

    if (resultData.pdfUrl) {
      const normalizedPath = resultData.pdfUrl.replace(/\\/g, '/');
      setBlobName(normalizedPath);
    } else {
      onError?.('PDFのURLが存在しません');
      return;
    }

    setDocumentInfo({
      insuranceCompany: resultData.companyName || getCompanyName(resultData),
      diseaseName: resultData.diseaseName || '',
    });

    if (resultData.pages && setCurrentPage) {
      const pageNum = Number.parseInt(resultData.pages, 10);
      if (!Number.isNaN(pageNum) && pageNum > 0) {
        setCurrentPage(pageNum);
      }
    }

    initializeHighlight?.(resultData);
  }, [initializeHighlight, onError, setCurrentPage]);

  /**
   * 会社タブ選択時の処理。
   *
   * 会社名を受け取り、その会社の選択中傷病（または先頭）の検索結果をアクティブに適用する。
   *
   * @param {string} companyName - 選択した保険会社名
   */
  const activateCompany = useCallback((companyName) => {
    const group = groupedResults.find((item) => item.companyName === companyName);
    if (!group || group.results.length === 0) return;

    const selectedKey = selectedDiseaseByCompany[companyName];
    const selectedResult = group.results.find((result) => result._key === selectedKey) ?? group.results[0];

    setActiveCompany(companyName);
    applyActiveResult(selectedResult);
  }, [applyActiveResult, groupedResults, selectedDiseaseByCompany]);

  /**
   * 傷病タブ選択時の処理。
   *
   * 傷病キーを受け取り、対応する検索結果をアクティブに適用する。
   *
   * @param {string} companyName - 対象の保険会社名
   * @param {string} diseaseKey - 選択した傷病キー
   */
  const activateDisease = useCallback((companyName, diseaseKey) => {
    const group = groupedResults.find((item) => item.companyName === companyName);
    if (!group) return;

    const selectedResult = group.results.find((result) => result._key === diseaseKey);
    if (!selectedResult) return;

    setSelectedDiseaseByCompany((prev) => ({
      ...prev,
      [companyName]: diseaseKey,
    }));
    setActiveCompany(companyName);
    applyActiveResult(selectedResult);
  }, [applyActiveResult, groupedResults]);

  /**
   * 詳細画面のブラウザタイトルを「ASFiNDER」に固定する
   */
  useEffect(() => {
    const previousTitle = defaultDocumentTitleRef.current || APP_TITLE;

    if (typeof document !== 'undefined') {
      document.title = APP_TITLE;
      return () => {
        document.title = previousTitle;
      };
    }
  }, []);

/**
   * 画面幅に応じてモバイル表示を切り替えるEffect
   * リサイズイベントを監視し、1024px以下でモバイルモードに切り替え
   */
  /**
   * 現在選択中の会社タブの検索結果をアクティブに適用する。
   *
   * activeCompany 変更時に同会社の選択中傷病（または先頭）の検索結果を画面に反映するために使用。
   */
  useEffect(() => {
    // windowsのresizeイベントハンドラ
    // > リサイズに応じてモバイル判定用のフラグを更新
    const handleResize = () => {
      if (typeof window === 'undefined') return;
      setIsMobile(window.innerWidth <= 1024);
    };

    // 初期値を設定
    handleResize();

    // イベントリスナー登録
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  /**
   * react-pdfライブラリの警告抑制Effect
   * 
   * pdfjs-distのバージョン不一致警告を抑制する。
   * 実際の動作に影響はないが、コンソールが汚れるため抑制。
   */
  useEffect(() => {
    const originalWarn = console.warn;
    console.warn = function (message) {
      if (typeof message === 'string' && message.includes('version') && message.includes('match')) {
        return;
      }
      originalWarn.apply(console, arguments);
    };

    // クリーンアップ: console.warnを元に戻す
    return () => {
      console.warn = originalWarn;
    };
  }, []);

  /**
   * セッション情報読み込みEffect
   * 
   * 以下の優先順位でデータソースから検索結果を読み込む:
   * 1. sessionStorage（ブラウザリロード時の復元）
   * 2. insuranceSearchStore（検索画面からの遷移）
   * 
   * どちらからも読み込めない場合はエラーをコールバック。
   */
  useEffect(() => {
    try {
      const sessionPayload = loadResultFromSessionStorage();
      const storagePayload = loadSearchResultFromStorage();
      const sourcePayload = sessionPayload || storagePayload;
      const normalizedResults = normalizeResultsPayload(sourcePayload);

      if (normalizedResults.length === 0) {
        onError?.('検索結果データが見つかりません。引受目安検索から検索してください。');
        return;
      }

      if (!sessionPayload) {
        saveResultToSessionStorage(sourcePayload);
      }

      const initialSelectedMap = {};
      const initialGroups = new Map();
      normalizedResults.forEach((result, idx) => {
        const companyName = getCompanyName(result);
        const diseaseName = result.diseaseName || '傷病名不明';
        const key = `${result.id ?? idx}-${companyName}-${diseaseName}-${result.pdfUrl ?? ''}`;
        if (!initialGroups.has(companyName)) {
          initialGroups.set(companyName, []);
        }
        initialGroups.get(companyName).push({ ...result, companyName, diseaseName, _key: key });
      });
      initialGroups.forEach((results, companyName) => {
        initialSelectedMap[companyName] = results[0]._key;
      });

      const initialCompany = initialGroups.keys().next().value;

      setAllResults(normalizedResults);
      setSelectedDiseaseByCompany(initialSelectedMap);
      setActiveCompany(initialCompany);
    } catch (error) {
      onError?.('初期化処理中にエラーが発生しました', error);
    }
  }, [onError]);

  /**
   * Effect: アクティブな会社に対応する検索結果を表示状態へ反映する
   * 会社切り替え時は会社ごとの選択中傷病を優先し、未選択の場合は先頭の傷病を選択する
   */
  useEffect(() => {
    if (!activeCompany || groupedResults.length === 0) return;
    const group = groupedResults.find((item) => item.companyName === activeCompany);
    if (!group || group.results.length === 0) return;

    const selectedKey = selectedDiseaseByCompany[activeCompany] ?? group.results[0]._key;
    const selectedResult = group.results.find((result) => result._key === selectedKey) ?? group.results[0];

    if (!selectedDiseaseByCompany[activeCompany]) {
      setSelectedDiseaseByCompany((prev) => ({
        ...prev,
        [activeCompany]: selectedResult._key,
      }));
    }

    applyActiveResult(selectedResult);
  }, [activeCompany, groupedResults, selectedDiseaseByCompany, applyActiveResult]);

  /**
   * ドキュメント情報アコーディオンの開閉を切り替える
   * モバイル表示時にドキュメント情報の表示/非表示を切り替えるために使用
   */
  const toggleDocInfoAccordion = useCallback(() => {
    setIsDocInfoAccordionOpen((prev) => !prev);
  }, []);

  return {
    detailResult,
    blobName,
    currentQueryId,
    documentInfo,
    isMobile,
    isDocInfoAccordionOpen,
    toggleDocInfoAccordion,
    groupedResults,
    activeCompany,
    activeCompanyResults,
    selectedDiseaseByCompany,
    activateCompany,
    activateDisease,
    setDetailResult,
    setBlobName,
  };
};

export default useInsuranceHikiukeSession;
